import { apiClient } from '@core/api/ApiClientFactory';
import { userService } from '@/services/userService';
import { ActiveLicenses } from '@/core/types';

export interface OrganisationApiModel {
    id?: string;
    organisationId?: string;
    name: string;
    type?: string;
    activeLicenses?: ActiveLicenses[];
    recycleBinRetentionDays?: number;
    deviceDisconnectNotificationRecipients?: string[];
    ftpSettings?: FtpSettings | null;
    fastLanerOrgId?: string | null;
}

export interface Organisation extends OrganisationApiModel {
    licensesDisplay?: string;
    usersDisplay?: string;
    usersCount?: number;
}

export interface OrganisationInput {
    id?: string;
    organisationId?: string;
    name: string;
    activeLicenses?: ActiveLicenses[];
    recycleBinRetentionDays?: number;
    deviceDisconnectNotificationRecipients?: string[];
    ftpSettings?: FtpSettings | null | undefined;
    fastLanerOrgId?: string | null | undefined;
}

export interface FtpSettings {
    username: string;
    password: string;
    host: string;
    port?: number;
}

export interface LicenseOption {
    license: ActiveLicenses;
    label: string;
}

export const LICENSE_OPTIONS: LicenseOption[] = [
    { license: ActiveLicenses.Rfid, label: 'RFID' },
    { license: ActiveLicenses.ShopAutomation, label: 'Shop Automation' },
    { license: ActiveLicenses.HybridLoc, label: 'HybridLoc' },
    { license: ActiveLicenses.SmartShelf, label: 'Smart Shelf' },
    { license: ActiveLicenses.FastLaner, label: 'FastLaner' }
];

export const organisationService = {
    async getOrganisations(): Promise<OrganisationApiModel[]> {
        try {
            const response = await apiClient.get<OrganisationApiModel[]>('organisation/getAll');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async getOrganisationsForDisplay(): Promise<Organisation[]> {
        try {
            const [organisations, users] = await Promise.all([
                this.getOrganisations(),
                userService.getAllForAllOrganisations()
            ]);

            // Count users for each organisation
            const userCountsByOrg = users.reduce(
                (acc, user) => {
                    const orgIds = user.organisationIds || [];

                    for (const orgId of orgIds) {
                        if (orgId) {
                            acc[orgId] = (acc[orgId] || 0) + 1;
                        }
                    }

                    return acc;
                },
                {} as Record<string, number>
            );

            return organisations.map((org) => {
                const userCount = userCountsByOrg[org.id || ''] || 0;
                return this.formatOrganisationForDisplay(org, userCount);
            });
        } catch (error) {
            throw error;
        }
    },

    async getOrganisation(id: string): Promise<OrganisationApiModel> {
        try {
            const response = await apiClient.get<OrganisationApiModel>(`organisation/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async getCurrentOrganisation(): Promise<OrganisationApiModel> {
        try {
            const response = await apiClient.get<OrganisationApiModel>('organisation');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async createOrganisation(organisation: OrganisationInput): Promise<string> {
        try {
            const response = await apiClient.post<string>('organisation', organisation);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async updateOrganisation(organisation: OrganisationInput): Promise<string> {
        try {
            const response = await apiClient.put<string>('organisation', organisation);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async deleteOrganisation(id: string): Promise<void> {
        try {
            await apiClient.delete(`organisation/${id}`);
        } catch (error) {
            throw error;
        }
    },

    validateOrganisation(organisation: OrganisationInput): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!organisation.name || organisation.name.trim() === '') {
            errors.push('Organisation name is required');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    },

    formatOrganisationForDisplay(organisation: OrganisationApiModel, userCount: number = 0): Organisation {
        let licensesDisplayText = '';
        if (organisation.activeLicenses && organisation.activeLicenses.length > 0) {
            licensesDisplayText = organisation.activeLicenses
                .map((license) => {
                    const option = LICENSE_OPTIONS.find((opt) => opt.license === license);
                    return option?.label || license;
                })
                .join(', ');
        } else {
            licensesDisplayText = organisation.activeLicenses?.toString() || '';
        }

        return {
            ...organisation,
            licensesDisplay: licensesDisplayText,
            usersDisplay: userCount.toString(),
            usersCount: userCount
        };
    }
};
